import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const RecentTransactionsTable = () => {
  const [transactions] = useState([
    {
      id: 'TX-2024-001247',
      type: 'batch_test',
      description: 'Batch QA-2024-1248 purity test results logged',
      timestamp: new Date(Date.now() - 15 * 60 * 1000),
      blockHash: '0x7f9fade1c0d57a7af66ab4ead79fade1c0d57a7af66ab4ead7c2c2eb7b11a91385',
      status: 'confirmed',
      gasUsed: '21,000',
      user: 'Dr. Sarah Chen'
    },
    {
      id: 'TX-2024-001246',
      type: 'equipment_calibration',
      description: 'HPLC System #1 calibration certificate uploaded',
      timestamp: new Date(Date.now() - 45 * 60 * 1000),
      blockHash: '0x8a1bfde2d1e68b8bg77bc5fbe8afde2d1e68b8bg77bc5fbe8d3d3fc8c22b02496',
      status: 'confirmed',
      gasUsed: '18,500',
      user: 'Tech. Michael Rodriguez'
    },
    {
      id: 'TX-2024-001245',
      type: 'adverse_event',
      description: 'Adverse event AE-2024-0893 severity classification updated',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      blockHash: '0x9b2cgef3e2f79c9ch88cd6gcf9bgef3e2f79c9ch88cd6gcf9e4e4gd9d33c13507',
      status: 'confirmed',
      gasUsed: '22,750',
      user: 'Dr. Emma Thompson'
    },
    {
      id: 'TX-2024-001244',
      type: 'regulatory_submission',
      description: 'EMA submission document hash verification completed',
      timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000),
      blockHash: '0xac3dhfg4f3g80d0di99de7hdf0chfg4f3g80d0di99de7hdf0f5f5he0e44d24618',
      status: 'confirmed',
      gasUsed: '19,200',
      user: 'Regulatory Officer Lisa Park'
    },
    {
      id: 'TX-2024-001243',
      type: 'batch_test',
      description: 'Batch QA-2024-1247 dissolution test results logged',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      blockHash: '0xbd4eigh5g4h91e1ej00ef8ieg1deigh5g4h91e1ej00ef8ieg1g6g6if1f55e35729',
      status: 'pending',
      gasUsed: '20,100',
      user: 'Dr. Sarah Chen'
    }
  ]);

  const getTransactionTypeConfig = (type) => {
    switch (type) {
      case 'batch_test':
        return {
          icon: 'FlaskConical',
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          label: 'Batch Test'
        };
      case 'equipment_calibration':
        return {
          icon: 'Settings',
          color: 'text-secondary',
          bgColor: 'bg-secondary/10',
          label: 'Equipment'
        };
      case 'adverse_event':
        return {
          icon: 'Shield',
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          label: 'Safety'
        };
      case 'regulatory_submission':
        return {
          icon: 'CheckCircle',
          color: 'text-success',
          bgColor: 'bg-success/10',
          label: 'Regulatory'
        };
      default:
        return {
          icon: 'Circle',
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          label: 'Unknown'
        };
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'confirmed':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          icon: 'CheckCircle',
          label: 'Confirmed'
        };
      case 'pending':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          icon: 'Clock',
          label: 'Pending'
        };
      case 'failed':
        return {
          color: 'text-error',
          bgColor: 'bg-error/10',
          icon: 'XCircle',
          label: 'Failed'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          icon: 'Circle',
          label: 'Unknown'
        };
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const truncateHash = (hash) => {
    return `${hash?.slice(0, 8)}...${hash?.slice(-8)}`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Recent Blockchain Transactions</h3>
          <p className="text-sm text-muted-foreground">Latest quality assurance data logged to blockchain</p>
        </div>
        <div className="flex items-center space-x-2">
          <button className="px-3 py-1 text-xs font-medium text-primary hover:bg-primary/5 border border-primary rounded-md transition-colors duration-150">
            View All
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Transaction
              </th>
              <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Type
              </th>
              <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Status
              </th>
              <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Block Hash
              </th>
              <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Time
              </th>
              <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                User
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions?.map((transaction) => {
              const typeConfig = getTransactionTypeConfig(transaction?.type);
              const statusConfig = getStatusConfig(transaction?.status);
              
              return (
                <tr key={transaction?.id} className="border-b border-border hover:bg-muted/30 transition-colors duration-150">
                  <td className="py-4 px-2">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-foreground">{transaction?.id}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {transaction?.description}
                      </p>
                    </div>
                  </td>
                  <td className="py-4 px-2">
                    <div className={`flex items-center space-x-2 px-2 py-1 rounded-full ${typeConfig?.bgColor} w-fit`}>
                      <Icon name={typeConfig?.icon} size={12} className={typeConfig?.color} />
                      <span className={`text-xs font-medium ${typeConfig?.color}`}>
                        {typeConfig?.label}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-2">
                    <div className={`flex items-center space-x-2 px-2 py-1 rounded-full ${statusConfig?.bgColor} w-fit`}>
                      <Icon name={statusConfig?.icon} size={12} className={statusConfig?.color} />
                      <span className={`text-xs font-medium ${statusConfig?.color}`}>
                        {statusConfig?.label}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-2">
                    <div className="space-y-1">
                      <p className="text-xs font-mono text-foreground">
                        {truncateHash(transaction?.blockHash)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Gas: {transaction?.gasUsed}
                      </p>
                    </div>
                  </td>
                  <td className="py-4 px-2">
                    <p className="text-xs text-muted-foreground">
                      {formatTimeAgo(transaction?.timestamp)}
                    </p>
                  </td>
                  <td className="py-4 px-2">
                    <p className="text-xs text-foreground">{transaction?.user}</p>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecentTransactionsTable;